import { useState, useRef, useEffect } from 'react';
import type { PlanElement } from '../services/api';

/**
 * Custom hook for managing plan element form state
 * 
 * Extracted from PlanEditor.tsx to reduce component complexity
 * and improve testability.
 */

export interface PlanElementFormState {
  // Form values
  navn: string;
  varighet: number;
  beskrivelse: string;
  førProduksjonsstart: boolean;
  
  // Form setters
  setNavn: (value: string) => void;
  setVariighet: (value: number) => void;
  setBeskrivelse: (value: string) => void;
  setFørProduksjonsstart: (value: boolean) => void;
  
  // Ref for autofocus
  navnInputRef: React.RefObject<HTMLInputElement | null>;
  
  // Helper functions
  resetForm: () => void;
  loadFromElement: (element: PlanElement) => void;
  isValid: () => boolean;
}

const DEFAULT_VARIGHET = 5;

export const usePlanElementForm = (): PlanElementFormState => {
  const [navn, setNavn] = useState('');
  const [varighet, setVariighet] = useState(DEFAULT_VARIGHET);
  const [beskrivelse, setBeskrivelse] = useState('');
  const [førProduksjonsstart, setFørProduksjonsstart] = useState(false);
  
  const navnInputRef = useRef<HTMLInputElement>(null);
  
  const resetForm = () => {
    setNavn('');
    setVariighet(DEFAULT_VARIGHET);
    setBeskrivelse('');
    setFørProduksjonsstart(false);
  };
  
  const loadFromElement = (element: PlanElement) => {
    setNavn(element.navn);
    setVariighet(element.varighet_minutter || DEFAULT_VARIGHET);
    setBeskrivelse(element.beskrivelse || '');
    setFørProduksjonsstart(element.før_produksjonsstart || false);
  };
  
  const isValid = () => {
    return navn.trim().length > 0;
  };
  
  return {
    navn,
    varighet,
    beskrivelse,
    førProduksjonsstart,
    setNavn,
    setVariighet,
    setBeskrivelse,
    setFørProduksjonsstart,
    navnInputRef,
    resetForm,
    loadFromElement,
    isValid,
  };
};

